/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { css, jsx, keyframes } from '@compiled/react';
import debounce from 'lodash/debounce';

import type { EditorAppearance } from '@atlaskit/editor-common/types';
import { findOverflowScrollParent } from '@atlaskit/editor-common/ui';
import type { Decoration, EditorView } from '@atlaskit/editor-prosemirror/view';
import { DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { akEditorFloatingPanelZIndex } from '@atlaskit/editor-shared-styles';
import { token } from '@atlaskit/tokens';

import type { EndExperience } from '../../../types/types';
import { useUpdateEditorHeight } from '../../hooks/useUpdateEditorHeight';
import type { InvokedFrom } from '../../utils/analytics/analyticsFlowTypes';
import { UI_SCROLL_MARGIN_TOP } from '../../utils/constants';

import { getModalLeft, getModalTop, getResizableOrBreakoutWrapper } from './utils';

/**
 * the z-index logic is duplicated in generative-ai-modal/UnhandledError
 */

// Where do we get this magic number 500?
// - The lowest product z-index we need to be below is 500 from the Feedback Collector.
// As well as the Editor toolbar popup menu, which is also 500.
// This allows us to be above the floating toolbar, but below the feedback collector.
// +2 because tables floating toolbar is +1.
const MAX_Z_INDEX = 500;

// Where do we get this magic number 402?
// - The highest product z-index we need to be above is 401 from the floating toolbar*.
// *in Confluence and Bitbucket. It is 600px in Atlas & 511 in Jira, however that is a more niche case than the Feedback Collector,
// so this is a known trade-off until we have a better solution.
const MIN_Z_INDEX = 402;

// Sets the z-index so it is not greater than the maximum, and less than the minimum.
const akAiModalRegionZIndex = Math.min(
	MAX_Z_INDEX,
	Math.max(MIN_Z_INDEX, akEditorFloatingPanelZIndex + 2),
);

const slideFadeIn = keyframes({
	from: { opacity: 0, transform: 'translateY(1rem)' },
	to: { opacity: 1, transform: 'translateY(0)' },
});

const modalStyles = css({
	marginTop: token('space.200'),
	position: 'absolute',
	zIndex: akAiModalRegionZIndex,
	marginRight: token('space.negative.200'),
	marginLeft: token('space.negative.200'),
	width: 'calc(var(--content-width) + 2rem)',
	animationName: slideFadeIn,
	animationDuration: '0.2s',
	animationTimingFunction: 'ease-out',
	animationFillMode: 'forwards',
	'@media (prefers-reduced-motion)': { animation: 'none' },
});

interface ModalRegionProps {
	appearance: EditorAppearance;
	autoScroll?: boolean;
	children: ReactNode;
	decoration: Decoration;
	decorationSet: DecorationSet;
	editorRelativeWrapper?: Element | null;
	editorView: EditorView;
	endExperience: EndExperience;
	isDirectionalPinningEnabled?: boolean;
	lastTriggeredFrom?: InvokedFrom;
}

const getModalDecorationElement = (editorView: EditorView) => {
	return (
		editorView?.dom.querySelector<HTMLElement>('.ai-modal-end-widget') ||
		editorView?.dom.querySelector<HTMLElement>('.ai-selection-node')
	);
};

export const ModalRegion = ({
	children,
	decoration,
	editorView,
	editorRelativeWrapper,
	appearance,
	endExperience,
	lastTriggeredFrom,
	decorationSet,
	autoScroll,
}: ModalRegionProps): JSX.Element | null => {
	// Ignored via go/ees005
	// eslint-disable-next-line @atlaskit/editor/no-as-casting
	const editorViewElement = editorView.dom as HTMLElement;

	const modalRef = useRef<HTMLDivElement>(null);
	// Widened to match `getModalLeft`'s `number | string` return; legacy always
	// takes the numeric branch, so behaviour is unchanged.
	const [modalLeft, setModalLeft] = useState<number | string>(0);
	const [modalTop, setModalTop] = useState(0);
	const [modalWidth, setModalWidth] = useState(0);
	const [transition, setTransition] = useState('');
	const scrollParentRef = useRef<HTMLElement | null>(null);
	// get nearest resizable wrapper or breakout container looking up from the ai decoration position
	const resizableOrBreakoutWrapper = useMemo(
		() => getResizableOrBreakoutWrapper(editorView, decoration.to),
		[editorView, decoration],
	);

	const modalDecorationElement = getModalDecorationElement(editorView);

	// EDF-161: https://product-fabric.atlassian.net/browse/EDF-161
	// If the editor has a max-height set, we need to set position relative for .ak-editor-content-area
	useEffect(() => {
		if (appearance === 'comment') {
			// editorRelativeWrapper is the closest but can be null
			// fallback to modalDecorationElement if editorRelativeWrapper is null
			let editorContentArea: HTMLElement | null = null;
			if (editorRelativeWrapper) {
				editorContentArea =
					editorRelativeWrapper.querySelector<HTMLElement>('.ak-editor-content-area');
			} else if (modalDecorationElement) {
				editorContentArea = modalDecorationElement.closest<HTMLElement>('.ak-editor-content-area');
			}

			if (editorContentArea) {
				const computedStyle = getComputedStyle(editorContentArea);
				const editorHasMaxHeight = computedStyle.maxHeight !== 'none';
				const originalPosition = computedStyle.position;

				if (editorHasMaxHeight) {
					editorContentArea.style.position = 'relative';
				}

				return () => {
					if (editorHasMaxHeight && editorContentArea) {
						editorContentArea.style.position = originalPosition;
					}
				};
			}
		}
	}, [modalDecorationElement, appearance, editorRelativeWrapper]);

	// listen for editor resize and update modal size and position
	useEffect(() => {
		const setModalPosition = () => {
			if (!modalRef.current || !modalDecorationElement || !modalDecorationElement.offsetParent) {
				// The modalDecorationElement.offsetParent is used when calculating the modal top postion. If it is null
				// the modal will flikcer from top to bottom, because the top position resets to 0.
				return;
			}

			let relativePositionElement = editorViewElement;

			// When layout resizes / breakout changes, resizableOrBreakoutWrapper remounts
			// We need to wait for resizableOrBreakoutWrapper to update before we can calculate modal positions
			// Return if resizableOrBreakoutWrapper doesn't exist in view as it hasn't been updated yet to avoid flickering
			if (resizableOrBreakoutWrapper && !editorViewElement.contains(resizableOrBreakoutWrapper)) {
				return;
			}
			// position relative to breakout element when its available, or otherwise relative to editor view
			relativePositionElement = resizableOrBreakoutWrapper || editorViewElement;

			const top = getModalTop({
				appearance,
				modalElement: modalRef.current,
				editorRelativeWrapper,
				modalDecorationElement,
				lastTriggeredFrom,
				editorView,
			});
			const left = getModalLeft({ relativePositionElement, modalElement: modalRef.current });

			setModalLeft(left);
			setModalTop(top);
			setModalWidth(relativePositionElement.offsetWidth);
			//setting width to be animated here to make animation happen only after first positioning
			if (!transition) {
				setTransition('all 0.1s ease-in');
			}
		};
		setModalPosition();
		const setModalPositionDebounced = debounce(() => {
			setModalPosition();
		}, 20);
		const resizeObserver = new ResizeObserver(setModalPositionDebounced);

		resizeObserver.observe(editorViewElement);

		// editorViewOffsetParent can be undefined when editor is set to display: none
		// (when Confluence switches to Preview mode)
		const editorViewOffsetParent = editorViewElement.offsetParent;
		if (editorViewOffsetParent) {
			// This resize observer is intended to fix the position of the modal region
			// when the config panel (for extensions or templates) is opened/closed
			resizeObserver.observe(editorViewOffsetParent);
		}

		// listen to any changes in the resizableOrBreakoutWrapper if modal was triggered in
		// layout or table with breakout / resizable table
		if (resizableOrBreakoutWrapper) {
			resizeObserver.observe(resizableOrBreakoutWrapper);
		}

		return () => {
			resizeObserver.disconnect();
			//clearing any potentially running debounced reposition
			setModalPositionDebounced.cancel();
		};
		// no need to rerun due to transition change
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		editorViewElement,
		modalDecorationElement,
		editorRelativeWrapper,
		resizableOrBreakoutWrapper,
	]);

	useEffect(() => {
		if (!modalRef.current) {
			return;
		}

		if (!scrollParentRef.current) {
			const scrollParentEl = findOverflowScrollParent(modalRef.current) || null;
			if (!scrollParentEl) {
				return;
			}
			scrollParentRef.current = scrollParentEl;
		}
	}, []);

	useEffect(() => {
		if (!autoScroll || !modalTop) {
			return;
		}

		const modalEl = modalRef.current;
		if (!modalEl || !modalDecorationElement) {
			return;
		}

		if (!scrollParentRef.current) {
			const scrollParentEl = findOverflowScrollParent(modalEl) || null;
			if (!scrollParentEl) {
				return;
			}
			scrollParentRef.current = scrollParentEl;
		}

		// The following values are all absoltue values this way they can all be easily compared to one another.
		const viewportTop = scrollParentRef.current.scrollTop;
		const viewportBottom =
			scrollParentRef.current.scrollTop +
			(scrollParentRef.current.offsetHeight - UI_SCROLL_MARGIN_TOP);
		const modalTippyTop = Math.max(0, modalTop - modalDecorationElement.offsetHeight);
		const modalBottom = modalTop + modalEl.offsetHeight;

		// Check if modal is already fully visible within the viewport.
		const isModalWithinViewport = modalTippyTop >= viewportTop && modalBottom <= viewportBottom;
		const isModalAboveViewport = modalTippyTop < viewportTop;
		// We should be able to surmise that if the modal is not within the viewport or above it, then it's below it.

		/**
		 * Check if modal height exceeds scroll parent's height
		 * Scroll margin is used to prevent the modal from being scrolled to the top
		 * and should match scroll margin in other modals e.g. from
		 * packages/editor/editor-plugin-ai/src/ui/components/FloatingContainer/styles.ts
		 */
		const isModalFullHeight =
			scrollParentRef.current.offsetHeight - UI_SCROLL_MARGIN_TOP < modalEl.scrollHeight;

		if (isModalWithinViewport) {
			// If the model fits in the view then we should not scroll at all
			return;
		}

		if (isModalFullHeight || isModalAboveViewport) {
			// If the modal is bigger then the viewport, or above it, then we will scroll to the top of it
			scrollParentRef.current.scroll({
				top: Math.max(0, modalTippyTop - UI_SCROLL_MARGIN_TOP),
				behavior: 'instant',
			});
			return;
		}

		// If the modal is not within the viewport, or above it, then it must off the bottom or below it. So we will
		// want to scroll the page down, just enough to bring the bottom of the modal into view. This should just be
		// a matter of gettting the delta between the btm of the modal and the btm of the viewport and adding that
		// onto the current viewport top.
		scrollParentRef.current.scroll({
			top: viewportTop + Math.max(modalBottom - viewportBottom, 0) + UI_SCROLL_MARGIN_TOP,
			behavior: 'instant',
		});
	}, [autoScroll, modalTop, modalDecorationElement]);

	// sync modal height with editor content area height
	useUpdateEditorHeight({
		modalElement: modalRef.current,
		editorViewElement,
		isInDocumentStreamingEnabled: false,
	});

	// Check for the modal decoration which is used for positioning
	// If there is none the modal should be removed from the DOM & return early
	// This is intended to make the code more resilient to bugs caused by the
	// editor AI plugin not being reinstantiated correctly when the editor is closed/opened
	// See https://product-fabric.atlassian.net/browse/EDITOR-52

	if (decorationSet === DecorationSet.empty) {
		endExperience();
		return null;
	}

	const style = {
		top: modalTop,
		transition,
		left: modalLeft,
		'--content-width': `${modalWidth}px`,
		display: modalWidth ? 'block' : 'none',
	};

	return (
		<div
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
			className="ak-editor-plugin-ai-modal"
			data-testid="ai-plugin-modal"
			css={[modalStyles]}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			style={style}
			ref={modalRef}
			// data-editor-popup is required so that clicks within the modal will be detected as clicks inside the editor,
			// Otherwise, content selection will be prevented in packages/editor/editor-core/src/ui/Addon/click-area-helper.ts
			data-editor-popup={true}
		>
			{children}
		</div>
	);
};
